import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, navigate } from '../../app/router';
import type { Org } from '../../lib/api';
import { Button, Dialog, Input, Select } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';
import { useInbox, useInvalidateContent, useProjects, withInbox } from '../dashboard/queries';
import { instantiateTemplate, type TemplateChoice } from './instantiate';
import { templateLabels } from './system';

/** "Use" from the gallery: a project of the organization and a title, then the new board opens. */
export function UseTemplateDialog({ org, choice, name, onClose }: { org: Org; choice: TemplateChoice; name: string; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const projects = useProjects(org.id);
  const inbox = useInbox(org.id);
  const options = withInbox(
    inbox.data,
    t('dashboard.noProject'),
    [...(projects.data ?? [])].filter((p) => !p.deletedAt && !p.archivedAt).sort((a, b) => a.name.localeCompare(b.name)),
  );
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState(name.slice(0, 200));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const target = projectId || options[0]?.id || '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !title.trim() || !target) return;
    setBusy(true);
    setError(false);
    try {
      const { boardId } = await instantiateTemplate({ api, orgId: org.id, projectId: target, folderId: null, title: title.trim(), choice, labels: templateLabels(t) });
      await invalidate();
      onClose();
      navigate(href('board', { boardId }));
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('boardTemplates.useTitle', { name })} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('dashboard.project')}
          <Select value={target} onChange={(e) => setProjectId(e.target.value)}>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('dashboard.boardTitle')}
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </label>
        {error && (
          <p role="alert" className="font-sans text-xs text-danger">
            {t('auth.genericError')}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <DialogClose>
            {t('dashboard.cancel')}
          </DialogClose>
          <Button variant="primary" type="submit" disabled={busy || !title.trim() || !target}>
            {t('dashboard.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
